import React, { useEffect, useState } from "react";
import {
  Table,
  Button,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  // Form,
  FormGroup,
  Label,
  Input,
  Nav,
  NavLink,
  NavItem,
  TabContent,
  TabPane,
  Form,
  FormFeedback,
} from "reactstrap";
import Select from "react-select";
import { SettingsProps } from "../../types/settings";
import { Formik, Form as FormikForm, useFormik } from "formik";
import { rolesValidation, usersPermissionsValidation } from "rules/settings";
import {
  useCreateStaff,
  useDeleteStaff,
  useGetStaff,
  useUpdateStaff,
} from "hooks/useStaff";
import { RoleType, StaffType } from "types";
import DeleteModal from "Components/Common/DeleteModal";
import classnames from "classnames";
import {
  useCreateRole,
  useDeleteRole,
  useGetRoles,
  useUpdateRole,
} from "hooks/useRoles";
import { PermissionsTable, UserDetailsModal } from "./components";
import { useGetBranches } from "hooks/useBranches";
import { useSelector } from "hooks/useSelector";

const UsersPermissions: React.FC<SettingsProps> = ({ onGlobalCancel }) => {
  const branch_id = useSelector((state) => state.branch.currentBranch?.id);
  const { data: staffList } = useGetStaff(branch_id);
  const { mutate: addStaff } = useCreateStaff();
  const { mutate: updateStaffInfo } = useUpdateStaff();
  const { data: branchData } = useGetBranches();
  const { mutate: deleteStaff } = useDeleteStaff();
  const { mutate: addRole } = useCreateRole();
  const { mutate: updateRole } = useUpdateRole();
  const { mutate: deleteRole } = useDeleteRole();
  const { data: userRolesList } = useGetRoles();
  const [modal, setModal] = useState(false);
  const [viewModal, setViewModal] = useState(false);
  const [rolesModalOpen, setRolesModalOpen] = useState(false);
  const [editDetails, setEditDetails] = useState<any>(null);
  const [roleDetails, setRoleDetails] = useState<any>(null);
  const [deleteModal, setDeleteModal] = useState<boolean>(false);
  const [deleteRoleModal, setDeleteRoleModal] = useState<boolean>(false);
  const [activeTab, setActiveTab] = useState<string | number>("users");
  const [selectedPermissions, setSelectedPermissions] = useState<any>();

  const roleOptions =
    userRolesList
      ?.filter((role) => role.name !== "SuperAdmin")
      .map((role) => ({
        value: role.id,
        label: role.name,
      })) || [];

  // const { permissions } = useSelector((state) => state.Login.permissions);
  // TODO:: enforce role based access control on users and permissions

  // Add a custom option to the roleOptions
  const customRoleOption = { value: "custom", label: "Custom" };
  const updatedRoleOptions = [...roleOptions, customRoleOption];

  const statusOptions = [
    { value: true, label: "Active" },
    { value: false, label: "Inactive" },
  ];

  const toggle = () => {
    setModal(!modal);
    if (modal) {
      setEditDetails(null);
      userValidation.resetForm();
    }
  };

  const toggleViewModal = () => {
    setViewModal(!viewModal);
    if (viewModal) {
      setEditDetails(null);
    }
  };

  const toggleRolesModal = () => {
    setRolesModalOpen(!rolesModalOpen);
    if (rolesModalOpen) {
      setRoleDetails(null);
    }
  };

  // change tab
  const toggleTab = (tab: any) => {
    if (activeTab !== tab) {
      setActiveTab(tab);
    }
  };

  const handleEdit = async (user: any) => {
    await setEditDetails(user);
    setModal(true);
  };

  const handleViewUser = (user: any) => {
    setEditDetails(user);
    setViewModal(true);
  };

  //delete user
  const onClickDelete = (user: any) => {
    setEditDetails(user);
    setDeleteModal(true);
  };

  const handleDelete = () => {
    if (editDetails.id) {
      deleteStaff(editDetails.id);
      setDeleteModal(false);
    }
  };

  const handleRoleEdit = (role: any) => {
    setRoleDetails(role);
    setRolesModalOpen(true);
  };

  const onClickRoleDelete = (role: any) => {
    setRoleDetails(role);
    setDeleteRoleModal(true);
  };

  const handleRoleDelete = () => {
    if (roleDetails.id) {
      deleteRole(roleDetails.id);
      setDeleteRoleModal(false);
    }
  };

  const initialValues: StaffType = editDetails
    ? {
        name: editDetails.name,
        email: editDetails.email,
        phone: editDetails.phone,
        branch_id: editDetails.branch_id,
        address: editDetails.address,
        password: editDetails.password,
        role_id: editDetails?.role?.id,
        is_active: editDetails.is_active,
      }
    : {
        name: "",
        email: "",
        phone: "",
        branch_id: 0,
        address: "",
        password: "",
        role_id: 0,
        is_active: true,
      };

  const initialRoleValues: RoleType = roleDetails
    ? {
        name: roleDetails.name,
        description: roleDetails.description,
        permissions: roleDetails.permissions,
      }
    : {
        name: "",
        description: "",
        permissions: {},
      };

  const handleSubmit = (values: any) => {
    const payload = {
      name: values.name,
      email: values.email,
      phone: values.phone,
      branch_id: values.branch_id,
      address: values.address,
      role_id: values.role_id,
      is_active: values.is_active,
      password: values.password,
    };

    if (editDetails) {
      updateStaffInfo({ id: editDetails.id, data: payload });
    } else {
      addStaff(payload);
    }
    setEditDetails(null);
    toggle();
  };

  const userValidation = useFormik<StaffType>({
    initialValues: initialValues,
    validationSchema: usersPermissionsValidation,
    onSubmit: (values) => handleSubmit(values),
  });

  const handleRoleSubmit = (values: any) => {
    const payload = {
      name: values.name,
      description: values.description,
      permissions: selectedPermissions,
    };

    if (roleDetails) {
      updateRole({ id: roleDetails.id, data: payload });
    } else {
      addRole(payload, {
        onSuccess: (newRole) => {
          userValidation.setFieldValue("role_id", newRole.id);
        },
      });
    }
    setEditDetails(null);
    toggleRolesModal();
  };

  useEffect(() => {
    if (editDetails) {
      userValidation.setValues({
        name: editDetails.name,
        email: editDetails.email,
        phone: editDetails.phone,
        branch_id: editDetails.branch_id,
        address: editDetails.address,
        password: editDetails.password,
        role_id: editDetails?.role?.id,
        is_active: editDetails.is_active,
      });

      console.log("Staff details: ", editDetails);
    }
  }, [editDetails]);

  return (
    <>
      <UserDetailsModal
        isOpen={viewModal}
        data={editDetails}
        toggle={toggleViewModal}
      />
      <Nav pills>
        <NavItem>
          <NavLink
            className={classnames({ active: activeTab === "users" })}
            onClick={() => {
              toggleTab("users");
            }}
          >
            <i className="bx bx-users font-size-20 d-sm-none" />
            <span className="d-none d-sm-block">Users</span>
          </NavLink>
        </NavItem>

        <NavItem>
          <NavLink
            className={classnames({ active: activeTab === "permissions" })}
            onClick={() => {
              toggleTab("permissions");
            }}
          >
            <i className="bx bx-book-content font-size-20 d-sm-none" />
            <span className="d-none d-sm-block">Roles and Permissions</span>
          </NavLink>
        </NavItem>
      </Nav>
      <TabContent activeTab={activeTab} className="py-4">
        <TabPane tabId="users">
          <div>
            <div className="d-flex justify-content-between align-items-center mb-4">
              <h4 className="mb-0">User Management</h4>
              <Button color="primary" onClick={toggle}>
                <i className="bx bx-plus me-1"></i> Add User
              </Button>
            </div>

            <Table responsive className="table-centered">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Email</th>
                  <th>Role</th>
                  <th>Status</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {staffList && staffList.length > 0
                  ? staffList.map((item) => (
                      <tr key={item.id}>
                        <td>{item.name}</td>
                        <td>{item.email}</td>
                        <td>{item.role.name}</td>
                        <td>
                          <span
                            className={`badge bg-${
                              item.is_active === true ? "success" : "danger"
                            }`}
                          >
                            {item.is_active ? "Active" : "In-Active"}
                          </span>
                        </td>
                        {item?.role?.name !== "SuperAdmin" && (
                          <td>
                            <Button
                              size="sm"
                              className="me-2 btn btn-sm btn-soft-info"
                              onClick={() => handleViewUser(item)}
                              disabled={item?.role?.name === "SuperAdmin"}
                            >
                              <i className="mdi mdi-eye-outline"></i>
                            </Button>
                            <Button
                              size="sm"
                              className="me-2 btn btn-sm btn-soft-info"
                              onClick={() => handleEdit(item)}
                              disabled={item?.role?.name === "SuperAdmin"}
                            >
                              <i className="bx bx-edit-alt"></i>
                            </Button>
                            <Button
                              className="me-2 btn btn-sm btn-soft-danger"
                              size="sm"
                              onClick={() => onClickDelete(item)}
                            >
                              <i className="bx bx-trash"></i>
                            </Button>
                          </td>
                        )}
                      </tr>
                    ))
                  : "No users found"}
              </tbody>
            </Table>

            {/* Add User Modal */}
            <Modal isOpen={modal} toggle={toggle} centered size="md">
              <ModalHeader toggle={toggle} className="border-0 pb-0">
                <h4 className="mb-0">
                  {editDetails ? "Edit User" : "Add New User"}
                </h4>
              </ModalHeader>
              <ModalBody className="pt-4">
                <Form>
                  <FormGroup className="mb-4">
                    <Label for="userName" className="form-label">
                      Name
                    </Label>
                    <Input
                      type="text"
                      name="name"
                      id="userName"
                      placeholder="Enter user's name"
                      className={`form-control ${
                        userValidation.touched.name &&
                        userValidation.errors.name
                          ? "is-invalid"
                          : ""
                      }`}
                      onChange={userValidation.handleChange}
                      onBlur={userValidation.handleBlur}
                      value={userValidation.values.name || ""}
                    />
                    {userValidation.touched.name &&
                      userValidation.errors.name && (
                        <div className="invalid-feedback">
                          {userValidation.errors.name}
                        </div>
                      )}
                  </FormGroup>

                  <FormGroup className="mb-4">
                    <Label for="userEmail" className="form-label">
                      Email
                    </Label>
                    <Input
                      type="email"
                      name="email"
                      id="userEmail"
                      placeholder="Enter user's email"
                      className={`form-control ${
                        userValidation.touched.email &&
                        userValidation.errors.email
                          ? "is-invalid"
                          : ""
                      }`}
                      onChange={userValidation.handleChange}
                      onBlur={userValidation.handleBlur}
                      value={userValidation.values.email}
                    />
                    {userValidation.touched.email &&
                      userValidation.errors.email && (
                        <div className="invalid-feedback">
                          {userValidation.errors.email}
                        </div>
                      )}
                  </FormGroup>
                  <FormGroup>
                    <div className="mb-3">
                      <Label htmlFor="branch_id">Branch *</Label>
                      <Input
                        type="select"
                        name="branch_id"
                        onChange={(e) =>
                          userValidation.setFieldValue(
                            "branch_id",
                            e.target.value
                          )
                        }
                        value={userValidation.values.branch_id}
                        invalid={
                          !!(
                            userValidation.touched.branch_id &&
                            userValidation.errors.branch_id
                          )
                        }
                      >
                        <option value="">Select Branch...</option>
                        {branchData &&
                          branchData.map((branch) => (
                            <option key={branch.id} value={branch.id}>
                              {branch.name}
                            </option>
                          ))}
                      </Input>
                      {userValidation.touched.branch_id &&
                        userValidation.errors.branch_id && (
                          <FormFeedback>
                            {userValidation.errors.branch_id}
                          </FormFeedback>
                        )}
                    </div>
                  </FormGroup>

                  <FormGroup className="mb-4">
                    <Label for="phone" className="form-label">
                      Phone
                    </Label>
                    <Input
                      type="text"
                      name="phone"
                      id="phone"
                      placeholder="Enter user's contact"
                      className={`form-control ${
                        userValidation.touched.phone &&
                        userValidation.errors.phone
                          ? "is-invalid"
                          : ""
                      }`}
                      onChange={userValidation.handleChange}
                      onBlur={userValidation.handleBlur}
                      value={userValidation.values.phone || ""}
                    />
                    {userValidation.touched.phone &&
                      userValidation.errors.phone && (
                        <div className="invalid-feedback">
                          {userValidation.errors.phone}
                        </div>
                      )}
                  </FormGroup>

                  <FormGroup className="mb-4">
                    <Label for="address" className="form-label">
                      Address
                    </Label>
                    <Input
                      type="text"
                      name="address"
                      id="address"
                      placeholder="Enter user's address"
                      className={`form-control ${
                        userValidation.touched.address &&
                        userValidation.errors.address
                          ? "is-invalid"
                          : ""
                      }`}
                      onChange={userValidation.handleChange}
                      onBlur={userValidation.handleBlur}
                      value={userValidation.values.address || ""}
                    />
                    {userValidation.touched.address &&
                      userValidation.errors.address && (
                        <div className="invalid-feedback">
                          {userValidation.errors.address}
                        </div>
                      )}
                  </FormGroup>

                  <FormGroup className="mb-4">
                    <Label for="password" className="form-label">
                      Password
                    </Label>
                    <Input
                      id="password"
                      name="password"
                      type="password"
                      value={userValidation.values.password}
                      onChange={(e) =>
                        userValidation.setFieldValue("password", e.target.value)
                      }
                      onBlur={userValidation.handleBlur}
                      className={
                        userValidation.touched.password &&
                        userValidation.errors.password
                          ? "is-invalid"
                          : ""
                      }
                    />
                    {userValidation.touched.password &&
                      userValidation.errors.password && (
                        <div className="invalid-feedback d-block">
                          {userValidation.errors.password}
                        </div>
                      )}
                  </FormGroup>

                  <FormGroup className="mb-4">
                    <Label for="role_id" className="form-label">
                      Role
                    </Label>
                    <Select
                      id="role_id"
                      name="role_id"
                      options={updatedRoleOptions}
                      value={
                        updatedRoleOptions.find(
                          (option) =>
                            option.value === userValidation.values.role_id
                        ) || null
                      }
                      onChange={(option) => {
                        if (option?.value === "custom") {
                          toggleRolesModal(); // Trigger the create role modal
                        } else {
                          userValidation.setFieldValue(
                            "role_id",
                            option?.value
                          );
                        }
                      }}
                      onBlur={userValidation.handleBlur}
                      className={
                        userValidation.touched.role_id &&
                        userValidation.errors.role_id
                          ? "is-invalid"
                          : ""
                      }
                      styles={{
                        control: (base, state) => ({
                          ...base,
                          borderColor:
                            userValidation.touched.role_id &&
                            userValidation.errors.role_id
                              ? "#dc3545"
                              : state.isFocused
                              ? "#0d9488"
                              : base.borderColor,
                          "&:hover": {
                            borderColor: "#0d9488",
                          },
                          boxShadow: state.isFocused
                            ? "0 0 0 1px #0d9488"
                            : base.boxShadow,
                        }),
                        option: (base, state) => ({
                          ...base,
                          backgroundColor: state.isSelected
                            ? "#0d9488"
                            : state.isFocused
                            ? "#f0fdfa"
                            : "white",
                          color: state.isSelected ? "white" : "#134e4a",
                          ":hover": {
                            backgroundColor: state.isSelected
                              ? "#0d9488"
                              : "#f0fdfa",
                          },
                        }),
                        menu: (base) => ({
                          ...base,
                          backgroundColor: "white",
                        }),
                      }}
                    />
                    {userValidation.touched.role_id &&
                      userValidation.errors.role_id && (
                        <div className="invalid-feedback d-block">
                          {userValidation.errors.role_id}
                        </div>
                      )}
                  </FormGroup>

                  {editDetails && (
                    <FormGroup className="mb-4">
                      <Label for="is_active" className="form-label">
                        Status
                      </Label>
                      <Select
                        id="is_active"
                        name="is_active"
                        value={
                          statusOptions.find(
                            (option) =>
                              option.value === userValidation.values.is_active
                          ) || null
                        }
                        options={statusOptions}
                        onChange={(option) =>
                          userValidation.setFieldValue(
                            "is_active",
                            option?.value
                          )
                        }
                        onBlur={userValidation.handleBlur}
                        className={
                          userValidation.touched.is_active &&
                          userValidation.errors.is_active
                            ? "is-invalid"
                            : ""
                        }
                        styles={{
                          control: (base, state) => ({
                            ...base,
                            borderColor:
                              userValidation.touched.is_active &&
                              userValidation.errors.is_active
                                ? "#dc3545"
                                : state.isFocused
                                ? "#0d9488"
                                : base.borderColor,
                            "&:hover": {
                              borderColor: "#0d9488",
                            },
                            boxShadow: state.isFocused
                              ? "0 0 0 1px #0d9488"
                              : base.boxShadow,
                          }),
                          option: (base, state) => ({
                            ...base,
                            backgroundColor: state.isSelected
                              ? "#0d9488"
                              : state.isFocused
                              ? "#f0fdfa"
                              : "white",
                            color: state.isSelected ? "white" : "#134e4a",
                            ":hover": {
                              backgroundColor: state.isSelected
                                ? "#0d9488"
                                : "#f0fdfa",
                            },
                          }),
                          menu: (base) => ({
                            ...base,
                            backgroundColor: "white",
                          }),
                        }}
                      />
                      {userValidation.touched.is_active &&
                        userValidation.errors.is_active && (
                          <div className="invalid-feedback d-block">
                            {userValidation.errors.is_active}
                          </div>
                        )}
                    </FormGroup>
                  )}
                </Form>
              </ModalBody>
              <ModalFooter className="border-0 pt-0">
                <Button
                  color="secondary"
                  onClick={toggle}
                  className="btn-soft-secondary"
                >
                  Cancel
                </Button>
                <Button
                  color="primary"
                  onClick={() => {
                    console.log("Errors: ", userValidation.errors);
                    userValidation.handleSubmit();
                  }}
                >
                  {editDetails ? "Update User" : "Add User"}
                </Button>
              </ModalFooter>
            </Modal>

            <DeleteModal
              show={deleteModal}
              onDeleteClick={handleDelete}
              onCloseClick={() => setDeleteModal(false)}
            />
          </div>
        </TabPane>
        <TabPane tabId="permissions">
          <div>
            <div className="d-flex justify-content-between align-items-center mb-4">
              <h4 className="mb-0">Roles & Permissions</h4>
              <Button color="primary" onClick={toggleRolesModal}>
                <i className="bx bx-plus me-1"></i> Add Role
              </Button>
            </div>

            <Table responsive className="table-centered">
              <thead>
                <tr>
                  <th>ID</th>
                  <th>Name</th>
                  <th>Description</th>
                  <th>Action</th>
                </tr>
              </thead>
              <tbody>
                {userRolesList && userRolesList.length > 0
                  ? userRolesList.map((item) => (
                      <tr key={item.id}>
                        <td>{item.id}</td>
                        <td>{item.name}</td>
                        <td>{item.description}</td>
                        {item?.name !== "SuperAdmin" && (
                          <td>
                            <Button
                              size="sm"
                              className="me-2 btn btn-sm btn-soft-info"
                              disabled={item?.name === "SuperAdmin"}
                              onClick={() => handleRoleEdit(item)}
                            >
                              <i className="bx bx-edit-alt"></i>
                            </Button>
                            <Button
                              size="sm"
                              className="me-2 btn btn-sm btn-soft-danger"
                              disabled={item?.name === "SuperAdmin"}
                              onClick={() => onClickRoleDelete(item)}
                            >
                              <i className="bx bx-trash"></i>
                            </Button>
                          </td>
                        )}
                      </tr>
                    ))
                  : "No roles found"}
              </tbody>
            </Table>

            {/* Add User Modal */}
            <Modal
              isOpen={rolesModalOpen}
              toggle={toggleRolesModal}
              centered
              size="lg"
            >
              <Formik<RoleType>
                initialValues={initialRoleValues}
                validationSchema={rolesValidation}
                onSubmit={handleRoleSubmit}
              >
                {({
                  values,
                  errors,
                  touched,
                  handleChange,
                  handleBlur,
                  setFieldValue,
                  handleSubmit,
                }) => (
                  <>
                    <ModalHeader
                      toggle={toggleRolesModal}
                      className="border-1 pb-3 mb-3"
                    >
                      <h4 className="mb-0">
                        {roleDetails ? "Edit Role" : "Add New Role"}
                      </h4>
                    </ModalHeader>
                    <ModalBody className="pt-0">
                      <FormikForm>
                        <FormGroup className="mb-4">
                          <Label for="userName" className="form-label">
                            Name
                          </Label>
                          <Input
                            type="text"
                            name="name"
                            id="userName"
                            placeholder="Enter role name"
                            className={`form-control ${
                              touched.name && errors.name ? "is-invalid" : ""
                            }`}
                            onChange={handleChange}
                            onBlur={handleBlur}
                            value={values.name || ""}
                          />
                          {touched.name && errors.name && (
                            <div className="invalid-feedback">
                              {errors.name}
                            </div>
                          )}
                        </FormGroup>

                        <FormGroup className="mb-4">
                          <Label for="userEmail" className="form-label">
                            Description
                          </Label>
                          <Input
                            type="text"
                            name="description"
                            id="description"
                            placeholder="Enter role description"
                            className={`form-control ${
                              touched.description && errors.description
                                ? "is-invalid"
                                : ""
                            }`}
                            onChange={handleChange}
                            onBlur={handleBlur}
                            value={values.description}
                          />
                          {touched.description && errors.description && (
                            <div className="invalid-feedback">
                              {errors.description}
                            </div>
                          )}
                        </FormGroup>
                        <PermissionsTable
                          selectedPermissions={roleDetails?.permissions}
                          setSelectedPermissions={setSelectedPermissions}
                        />
                      </FormikForm>
                    </ModalBody>
                    <ModalFooter className="border-0 pt-0">
                      <Button
                        color="secondary"
                        onClick={toggleRolesModal}
                        className="btn-soft-secondary"
                      >
                        Cancel
                      </Button>
                      <Button
                        color="primary"
                        onClick={() => {
                          handleSubmit();
                        }}
                      >
                        {roleDetails ? "Update Role" : "Add Role"}
                      </Button>
                    </ModalFooter>
                  </>
                )}
              </Formik>
            </Modal>

            <DeleteModal
              show={deleteRoleModal}
              onDeleteClick={handleRoleDelete}
              onCloseClick={() => setDeleteRoleModal(false)}
            />
          </div>
        </TabPane>
      </TabContent>
    </>
  );
};

export default UsersPermissions;
